import { useState, useCallback, useRef } from 'react';
import GeocodingService from '../../service/vehicle/GeocodingService';
import { useNotification, NotificationSeverity } from '../../context/NotificationContext';

export const useGeocoding = () => {
  const [loadingStates, setLoadingStates] = useState(new Map());
  const [addressCache, setAddressCache] = useState(new Map());
  const { showNotification } = useNotification();
  const abortControllersRef = useRef(new Map());

  /**
   * Geocodifica coordenadas específicas con cache local y gestión de cancelación
   * @param {number} latitude - Latitud de la ubicación
   * @param {number} longitude - Longitud de la ubicación
   * @param {string} vehicleId - ID del vehículo (opcional)
   * @returns {Promise<Object>} Resultado de geocodificación
   */
  const geocodeLocation = useCallback(async (latitude, longitude, vehicleId = null) => {
    const locationKey = `${latitude}_${longitude}`;
    
    // Prevenir geocodificación duplicada
    if (loadingStates.get(locationKey)) {
      return null;
    }

    // Verificar cache local (5 minutos de validez)
    const cached = addressCache.get(locationKey);
    if (cached && (Date.now() - cached.timestamp) < 300000) {
      return cached.result;
    }

    // Configurar cancelación de petición
    const abortController = new AbortController();
    abortControllersRef.current.set(locationKey, abortController);

    // Actualizar estado de carga
    setLoadingStates(prev => new Map(prev).set(locationKey, true));

    try {
      const result = await GeocodingService.geocodeCoordinates(
        latitude, 
        longitude, 
        vehicleId
      );

      // Actualizar cache local con timestamp
      setAddressCache(prev => new Map(prev).set(locationKey, {
        result,
        timestamp: Date.now()
      }));

      if (result.success) {
        showNotification({
          message: `📍 Ubicación obtenida para ${vehicleId || 'vehículo'}: ${result.address.substring(0, 50)}...`,
          severity: NotificationSeverity.SUCCESS,
          autoHideDuration: 4000
        });
      } else {
        throw new Error(result.error || 'Error desconocido');
      }

      return result;

    } catch (error) {
      showNotification({
        message: `Error obteniendo ubicación: ${error.message}`,
        severity: NotificationSeverity.ERROR,
        autoHideDuration: 5000
      });

      return {
        success: false,
        error: error.message,
        address: 'Error obteniendo ubicación',
        source: 'error'
      };

    } finally {
      // Limpiar estado de carga
      setLoadingStates(prev => {
        const newMap = new Map(prev);
        newMap.delete(locationKey);
        return newMap;
      });

      // Limpiar controlador de cancelación
      abortControllersRef.current.delete(locationKey);
    }
  }, [showNotification, loadingStates, addressCache]);

  /**
   * Cancela geocodificación en progreso para coordenadas específicas
   * @param {number} latitude - Latitud de la ubicación
   * @param {number} longitude - Longitud de la ubicación
   */
  const cancelGeocoding = useCallback((latitude, longitude) => {
    const locationKey = `${latitude}_${longitude}`;
    const controller = abortControllersRef.current.get(locationKey);
    
    if (controller) {
      controller.abort();
    }
  }, []);

  /**
   * Limpia cache local y del servicio de geocodificación
   */
  const clearCache = useCallback(() => {
    setAddressCache(new Map());
    GeocodingService.clearCache();
  }, []);

  /**
   * Obtiene estadísticas del hook y servicio de geocodificación
   * @returns {Object} Estadísticas completas del sistema
   */
  const getStats = useCallback(() => {
    return {
      ...GeocodingService.getStats(),
      localCacheSize: addressCache.size,
      currentlyLoading: loadingStates.size
    };
  }, [addressCache.size, loadingStates.size]);

  /**
   * Verifica si una ubicación está siendo geocodificada
   * @param {number} latitude - Latitud de la ubicación
   * @param {number} longitude - Longitud de la ubicación
   * @returns {boolean} Estado de carga
   */
  const isLoading = useCallback((latitude, longitude) => {
    const locationKey = `${latitude}_${longitude}`;
    return loadingStates.get(locationKey) || false;
  }, [loadingStates]);

  return {
    geocodeLocation,
    cancelGeocoding,
    clearCache,
    getStats,
    isLoading,
    loadingCount: loadingStates.size
  };
};

export default useGeocoding;